'use no memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { usePaginationSearch } from '@/lib/listPage';
import { useSequenceSidebarData } from '@/routes/_chat/chat/$sessionId/-query';
import { IndividualSidebar } from '../../-components/IndividualSidebar';
import { ApprovalDrawer } from './-components/ApprovalDrawer';
import { ApprovalStatusTabs } from './-components/ApprovalStatusTabs';
import { useApprovalsTable } from './-hooks/useTable';
import { useApprovalCountsQuery } from './-query';
import type { ApprovalStatusFilter } from './-query/key';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_individual/individual/portal/approvals/')({
  validateSearch: zodValidator(SearchSchema),
  component: ApprovalsPage
});
function NoApprovalsMessage({
  status
}: {
  status: ApprovalStatusFilter;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.approvals.empty'
  });
  return <div className="flex flex-col items-center justify-center gap-3 py-12">
      <div className="flex h-10 w-10 items-center justify-center text-neutral-muted-on-fill">
        <Icon iconName="StampIcon" size={IconSize.XXL} inheritColor />
      </div>
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        {t(status)}
      </Text>
    </div>;
}
function ApprovalsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({
    from: Route.fullPath
  });
  const sidebarData = useSequenceSidebarData();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const {
    isDesktop
  } = useDesktopOrMobileMediaQuery();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.approvals'
  });
  const [pagination, setPagination] = usePaginationSearch({
    search: {
      ...search,
      pageIndex: search.page - 1,
      pageSize: search.pageSize
    },
    onSearchChange: next => navigate({
      search: prev => ({
        ...prev,
        page: next.pageIndex + 1,
        pageSize: next.pageSize
      })
    })
  });
  const {
    table,
    displayState,
    totalCount
  } = useApprovalsTable({
    status: search.status,
    emptyContent: <NoApprovalsMessage status={search.status} />,
    pagination,
    onPaginationChange: setPagination
  });
  const {
    data: counts
  } = useApprovalCountsQuery();
  const setStatus = (status: ApprovalStatusFilter) => navigate({
    search: () => ({
      status,
      selectedId: undefined,
      page: 1,
      pageSize: search.pageSize
    })
  });
  const openDrawer = (id: string) => navigate({
    search: s => ({
      ...s,
      selectedId: id
    })
  });
  const closeDrawer = () => navigate({
    search: s => ({
      ...s,
      selectedId: undefined
    })
  });
  return <div className="flex h-full min-h-0 w-full overflow-hidden">
      {isDesktop && <IndividualSidebar mode="portal" open={sidebarOpen} onToggle={() => setSidebarOpen(v => !v)} recentSessions={sidebarData.data?.recentSessions ?? []} upcomingInteractions={sidebarData.data?.upcomingInteractions ?? []} />}
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-neutral-bottom-fill">
        <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
          <span className="text-neutral-on-fill">
            <Icon iconName="StampIcon" size={IconSize.XL} inheritColor />
          </span>
          <Text as="h1" variant="h3">
            {t('pageTitle')}
          </Text>
        </div>
        <VStack className="min-h-0 flex-1 gap-4 bg-blue-50/30 px-5 py-6 md:px-12 md:py-8">
          <ApprovalStatusTabs value={search.status} counts={counts} onChange={setStatus} />
          <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <DataTable table={table} displayState={displayState} onRowClick={row => openDrawer(row.original.id)} isHoverable />
          </Card>
          <DataTablePagination table={table} totalCount={totalCount} pageOptions={[10, 20, 50, 100]} />
        </VStack>
      </main>

      <ApprovalDrawer approvalId={search.selectedId} open={Boolean(search.selectedId)} onClose={closeDrawer} />
    </div>;
}